import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {

  FaCalculator,
  FaChartLine,
  FaFileInvoiceDollar,
  FaHandshake,
  FaLaptopCode,
  FaUserTie,
  FaBalanceScale,
  FaMoneyBillWave,
  FaClipboardList,
  FaUsers,
  FaClock,
  FaCheck,
  FaRegLightbulb,
  FaShieldAlt,
  FaUniversity,
  FaMobileAlt

} from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import styled, { keyframes } from "styled-components";

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

// Styled Components
const SectionTitle = styled.h3`
  position: relative;
  font-weight: bold;
  margin-bottom: 40px;
  padding-bottom: 15px;
  text-align: center;
  
  &:after {
    content: '';
    position: absolute;
    width: 80px;
    height: 4px;
    background: linear-gradient(to right, #228B22, #E60000);
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 2px;
  }
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  height: 100%;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  border-top: 5px solid transparent;
  border-image: linear-gradient(to right, #228B22, #E60000);
  border-image-slice: 1;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
  }
`;

const ServiceIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 25px;
  background: ${props => props.bgColor || 'linear-gradient(to right, #228B22, #E60000)'};
  color: white;
  font-size: 32px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;

  &:hover {
    animation: ${pulse} 1s infinite;
  }
`;

const ServiceTitle = styled.h4`
  font-weight: bold;
  margin-bottom: 20px;
  color: #333;
  font-size: 22px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
  font-size: 16px;
  color: #555;
  
  svg {
    color: #228B22;
    margin-right: 15px;
    margin-top: 4px;
    flex-shrink: 0;
  }
`;

const SubFeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
  font-size: 15px;
  color: #666;
  margin-left: 30px;
  
  svg {
    color: #E60000;
    margin-right: 10px;
    margin-top: 4px;
    flex-shrink: 0;
    font-size: 12px;
  }
`;

const HeroSection = styled.section`
  position: relative;
  overflow: hidden;
  padding: 100px 0;
  background: linear-gradient(to right, rgba(0, 138, 68, 0.9), rgba(220, 53, 69, 0.9)), 
              url("/assets/images/business.jpg");
  background-size: cover;
  background-position: center;
  color: white;
`;

const HeroContent = styled.div`

  text-align: left;
  max-width: 800px;
 

  animation: ${fadeIn} 1s ease-in-out;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: bold;
  margin-bottom: 20px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const HeroSubtitle = styled.p`
  font-size: 1.5rem;
  margin-bottom: 30px;
  font-style: italic;
  text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.3);
`;

const HeroDescription = styled.p`
  font-size: 1.2rem;
  margin-bottom: 40px;
  line-height: 1.7;
`;

const ActionButton = styled.button`
  background: white;
  color: #333;
  border: none;
  padding: 15px 30px;
  border-radius: 50px;
  font-weight: bold;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
    background: #f8f8f8;
  }
`;

const InfoCard = styled.div`
  background: white;
  border-radius: 15px;
  padding: 30px;
  margin-top: -80px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 10;
`;

const InfoCardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoCardItem = styled.div`
  display: flex;
  align-items: center;
  
  svg {
    color: ${props => props.iconColor || '#228B22'};
    font-size: 28px;
    margin-right: 15px;
  }
  
  div {
    h5 {
      font-weight: bold;
      margin-bottom: 5px;
      color: #333;
    }
    
    p {
      margin: 0;
      color: #666;
    }
  }
`;

const TestimonialSection = styled.section`
  background: #f8f9fa;
  padding: 80px 0;
`;

const TestimonialCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 25px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  position: relative;
  
  &:before {
    content: '"';
    position: absolute;
    top: 20px;
    left: 20px;
    font-size: 80px;
    color: #f0f0f0;
    font-family: Georgia, serif;
    line-height: 1;
    z-index: 0;
  }

  p {
    position: relative;
    z-index: 1;
    font-style: italic;
    color: #555;
  }
  
  .testimonial-author {
    display: flex;
    align-items: center;
    margin-top: 20px;
    
    img {
      width: 50px;
      height: 50px;
      border-radius: 50%;
      object-fit: cover;
      margin-right: 15px;
    }
    
    div {
      h5 {
        margin: 0;
        font-weight: bold;
      }
      
      small {
        color: #777;
      }
    }
  }
`;

const ContactCard = styled.div`
  background: linear-gradient(to right, #228B22, #E60000);
  color: white;
  padding: 40px;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  text-align: center;
  margin: 60px auto;
  max-width: 800px;

  h4 {
    font-weight: bold;
    margin-bottom: 20px;
    font-size: 24px;
  }

  p {
    margin-bottom: 30px;
    font-size: 18px;
    line-height: 1.6;
  }

  button {
    background: white;
    color: #333;
    border: none;
    padding: 12px 30px;
    border-radius: 50px;
    font-weight: bold;
    transition: all 0.3s ease;
    font-size: 18px;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
    }
  }
`;

const EntrepreneursPage = () => {

  return (
    <>
      <Header />

      <HeroSection>
        <div className="container">
          <HeroContent>
            <HeroTitle>VOTRE PARTENAIRE EXPERT-COMPTABLE</HeroTitle>
            <HeroSubtitle>Pour une gestion sereine et performante</HeroSubtitle>
            <HeroDescription>
              Entrepreneurs, Professions Libérales, Indépendants : Concentrez-vous sur votre activité,
              nous gérons le reste.
            </HeroDescription>
            <ActionButton>Demander un devis gratuit</ActionButton>
          </HeroContent>
        </div>
      </HeroSection>

      {/* <section className="container">
        <InfoCard>
          <InfoCardGrid>
            <InfoCardItem iconColor="#228B22">
              <FaClock />
              <div>
                <h5>Gagnez du temps</h5>
                <p>Concentrez-vous sur votre cœur de métier</p>
              </div>
            </InfoCardItem>
            <InfoCardItem iconColor="#E60000">
              <FaShieldAlt />
              <div>
                <h5>Sécurisez votre activité</h5>
                <p>Conformité juridique, fiscale et sociale</p>
              </div>
            </InfoCardItem>
            <InfoCardItem iconColor="#228B22">
              <FaChartLine />
              <div>
                <h5>Optimisez votre performance</h5>
                <p>Conseil personnalisé et stratégique</p>
              </div>
            </InfoCardItem>
          </InfoCardGrid>
        </InfoCard>
      </section> */}

      <section className="service-content py-5 mt-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 mx-auto text-center mb-5">
              <h2 className="fw-bold">Nos Services Adaptés à Vos Besoins</h2>
              <div className="separator mx-auto my-3" style={{ height: "4px", width: "80px", background: "linear-gradient(to right, #228B22, #E60000)", borderRadius: "2px" }}></div>
              <p className="lead text-muted">
                Que vous soyez artisan, commerçant, profession libérale, freelance, ou dirigeant de TPE/PME,
                nous vous proposons une gamme complète de services, modulables en fonction de votre structure
                et de vos objectifs. CINEF-EC vous libère des contraintes administratives, comptables,
                fiscales et sociales, et vous apporte un conseil personnalisé pour optimiser la gestion
                et le développement de votre entreprise. Nous sommes bien plus qu'un simple cabinet d'expertise comptable ;
                nous sommes votre partenaire de confiance, à vos côtés à chaque étape.
              </p>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-lg-6">
              <ServiceCard>
                <ServiceIcon bgColor="linear-gradient(135deg, #228B22, #32CD32)">
                  <FaCalculator />
                </ServiceIcon>
                <ServiceTitle>Comptabilité et fiscalité</ServiceTitle>
                <FeatureList>
                  <FeatureItem>
                    <FaCheck /> <strong>Tenue de comptabilité (complète ou partagée)</strong>
                    <FeatureList>
                      <SubFeatureItem>
                        <FaCheck /> Saisie de vos opérations courantes
                      </SubFeatureItem>
                      <SubFeatureItem>
                        <FaCheck /> Gestion des notes de frais
                      </SubFeatureItem>
                      <SubFeatureItem>
                        <FaCheck /> Rapprochements bancaires
                      </SubFeatureItem>
                      <SubFeatureItem>
                        <FaCheck /> Révision des comptes
                      </SubFeatureItem>
                    </FeatureList>
                  </FeatureItem>
                  <FeatureItem>
                    <FaCheck /> <strong>Établissement des comptes annuels</strong> : Bilan, compte de résultat, annexe, liasse fiscale
                  </FeatureItem>
                  <FeatureItem>
                    <FaCheck /> <strong>Déclarations fiscales</strong>
                    <FeatureList>
                      <SubFeatureItem>
                        <FaCheck /> TVA (tous régimes)
                      </SubFeatureItem>
                      <SubFeatureItem>
                        <FaCheck /> Impôt sur le revenu (IR) ou impôt sur les sociétés (IS)
                      </SubFeatureItem>
                      <SubFeatureItem>
                        <FaCheck /> Autres taxes professionnelles
                      </SubFeatureItem>
                    </FeatureList>
                  </FeatureItem>
                  <FeatureItem>
                    <FaCheck /> <strong>Optimisation fiscale</strong> : Réduction de votre charge fiscale
                  </FeatureItem>
                  <FeatureItem>
                    <FaCheck /> <strong>Assistance en cas de contrôle fiscal</strong> : Préparation et défense
                  </FeatureItem>
                </FeatureList>
              </ServiceCard>
            </div>

            <div className="col-lg-6">
              <ServiceCard>
                <ServiceIcon bgColor="linear-gradient(135deg, #E60000, #FF6B6B)">
                  <FaUsers />
                </ServiceIcon>
                <ServiceTitle>Gestion sociale et paie</ServiceTitle>
                <FeatureList>
                  <FeatureItem>
                    <FaCheck /> <strong>Gestion de la paie</strong>
                    <FeatureList>
                      <SubFeatureItem>
                        <FaCheck /> Établissement des bulletins de paie
                      </SubFeatureItem>
                      <SubFeatureItem>
                        <FaCheck /> Calcul des cotisations sociales
                      </SubFeatureItem>
                      <SubFeatureItem>
                        <FaCheck /> Gestion des congés, absences, etc.
                      </SubFeatureItem>
                    </FeatureList>
                  </FeatureItem>
                  <FeatureItem>
                    <FaCheck /> <strong>Déclarations sociales</strong> : DSN et autres obligations sociales
                  </FeatureItem>
                  <FeatureItem>
                    <FaCheck /> <strong>Gestion des contrats de travail</strong> : Rédaction, formalités d'embauche, ruptures
                  </FeatureItem>
                  <FeatureItem>
                    <FaCheck /> <strong>Conseil en droit social</strong> : Application de la convention collective, gestion des relations
                  </FeatureItem>
                </FeatureList>
              </ServiceCard>
            </div>

            <div className="col-lg-6">
              <ServiceCard>
                <ServiceIcon bgColor="linear-gradient(135deg, #228B22, #32CD32)">
                  <FaChartLine />
                </ServiceIcon>
                <ServiceTitle>Conseil en gestion et pilotage</ServiceTitle>
                <FeatureList>
                  <FeatureItem>
                    <FaCheck /> <strong>Tableaux de bord personnalisés</strong> : Suivi de vos indicateurs clés de performance
                  </FeatureItem>
                  <FeatureItem>
                    <FaCheck /> <strong>Comptabilité analytique</strong> : Analyse de la rentabilité par produit, service, client
                  </FeatureItem>
                  <FeatureItem>
                    <FaCheck /> <strong>Budgets prévisionnels</strong> : Élaboration et suivi pour anticiper
                  </FeatureItem>
                  <FeatureItem>
                    <FaCheck /> <strong>Analyse financière</strong> : Diagnostic et identification des axes d'amélioration
                  </FeatureItem>
                  <FeatureItem>
                    <FaCheck /> <strong>Conseil en gestion de trésorerie</strong> : Optimisation et anticipation
                  </FeatureItem>
                  <FeatureItem>
                    <FaCheck /> <strong>Conseil en financement</strong> : Recherche de solutions pour vos projets
                  </FeatureItem>
                  <FeatureItem>
                    <FaCheck /> <strong>Accompagnement à la création/reprise d'entreprise</strong>
                  </FeatureItem>
                </FeatureList>
              </ServiceCard>
            </div>

            <div className="col-lg-6">
              <div className="row g-4 h-100">
                <div className="col-12">
                  <ServiceCard>
                    <ServiceIcon bgColor="linear-gradient(135deg, #E60000, #FF6B6B)">
                      <FaBalanceScale />
                    </ServiceIcon>
                    <ServiceTitle>Conseil juridique</ServiceTitle>
                    <FeatureList>
                      <FeatureItem>
                        <FaCheck /> <strong>Choix de la forme juridique</strong> (création ou transformation)
                      </FeatureItem>
                      <FeatureItem>
                        <FaCheck /> <strong>Secrétariat juridique</strong> (assemblées générales, formalités)
                      </FeatureItem>
                      <FeatureItem>
                        <FaCheck /> <strong>Conseil en droit des affaires et droit fiscal</strong>
                      </FeatureItem>
                    </FeatureList>
                  </ServiceCard>
                </div>
                <div className="col-12 mt-4">
                  <ServiceCard>
                    <ServiceIcon bgColor="linear-gradient(135deg, #228B22, #32CD32)">
                      <FaLaptopCode />
                    </ServiceIcon>
                    <ServiceTitle>Services en ligne et outils numériques</ServiceTitle>
                    <FeatureList>
                      <FeatureItem>
                        <FaCheck /> <strong>Plateforme collaborative en ligne</strong> : Accès 24h/24 et 7j/7
                      </FeatureItem>
                      <FeatureItem>
                        <FaCheck /> <strong>Logiciel de facturation et de gestion commerciale</strong>
                      </FeatureItem>
                      <FeatureItem>
                        <FaCheck /> <strong>Application mobile</strong> : Suivi de votre activité partout
                      </FeatureItem>
                    </FeatureList>
                  </ServiceCard>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <TestimonialSection>
        <div className="container">
          <SectionTitle>Ils nous font confiance</SectionTitle>
          <div className="row g-4">
            <div className="col-md-4">
              <TestimonialCard>
                <p>"CINEF-EC a complètement transformé notre gestion financière. Leur expertise nous a permis d'optimiser notre fiscalité tout en nous offrant une vision claire de nos performances."</p>
                <div className="testimonial-author">
                  <img src="../assets/images/image3.png" alt="Client" />
                  <div>
                    <h5>Sophie M.</h5>
                    <small>Dirigeante de PME</small>
                  </div>
                </div>
              </TestimonialCard>
            </div>
            <div className="col-md-4">
              <TestimonialCard>
                <p>"En tant que médecin libéral, je peux enfin me concentrer sur mes patients. L'équipe de CINEF-EC gère toute ma comptabilité et mes obligations fiscales avec rigueur et professionnalisme."</p>
                <div className="testimonial-author">
                  <img src="../assets/images/image2.png" alt="Client" />
                  <div>
                    <h5>Dr. Thomas L.</h5>
                    <small>Profession libérale</small>
                  </div>
                </div>
              </TestimonialCard>
            </div>
            <div className="col-md-4">
              <TestimonialCard>
                <p>"Les outils numériques proposés par CINEF-EC m'ont permis de gagner un temps précieux. Leur application mobile me permet de suivre mon activité en temps réel, même en déplacement."</p>
                <div className="testimonial-author">
                  <img src="../assets/images/image1.png" alt="Client" />
                  <div>
                    <h5>Jean-Marc P.</h5>
                    <small>Artisan</small>
                  </div>
                </div>
              </TestimonialCard>
            </div>
          </div>
        </div>
      </TestimonialSection>



      {/* <ContactCard>
        <h4>Vous souhaitez simplifier votre gestion et optimiser votre performance ?</h4>
        <p>
          Contactez-nous dès aujourd'hui pour un entretien gratuit et sans engagement.
          Nous étudierons ensemble vos besoins et vous proposerons une solution personnalisée.
        </p>
        <Link to="/contact" className="btn">Prendre rendez-vous</Link>
      </ContactCard> */}

      <Footer />

      <style>{`

        .separator {
          height: 4px;
          width: 80px;
          background: linear-gradient(to right, #228B22, #E60000);
          border-radius: 2px;
        }
        
        .lead {
          font-size: 1.2rem;
          line-height: 1.6;
        }
      `}</style>

    </>
  );

};

export default EntrepreneursPage;